<script setup>
import { useRadioAudio } from "@/composables/useRadioAudio";
import { escuchaLaEmision, dejaDeEscuchar } from "@/services/id3Emision";
import { ref, watch, onMounted, onUnmounted } from "vue";
import { usePlayerStore } from "@/stores/player";
import { fetchNowPlaying } from "@/services/api";
import { getCurrentShow } from "@/services/currentShow";
import { formatRange } from "@/utils/timeUtils";
import useCurrentStation from "@/composables/useCurrentStation";
import { useBackgroundEffect } from "@/composables/useBackgroundEffect";
import Hls from "hls.js";
import PlayerModal from "./PlayerModal.vue";
import { PLACEHOLDER as astraPlaceHolder, arte } from "@/services/placeholder";

const store = usePlayerStore();
const audioPlayer = ref(null);
const nowPlaying = ref(null);
const hls = ref(null); // 🎬 Instancia de HLS.js

// Cuándo se intentó por última vez la recuperación que NO toca el elemento.
// Sirve para no gastar la bala destructiva a la primera. Ver el manejo de
// MEDIA_ERROR más abajo.
let ultimoIntentoSuave = 0;

// 🔄 NUEVO: Usar el estado global reactivo
const { currentStationId, currentStation } = useCurrentStation();
const { setShowImage } = useBackgroundEffect();

const currentShow = ref({});
const isLoading = ref(false);
const showPlayerModal = ref(false);
const playerContainer = ref(null);
let intervalId = null;

// Cerrar panel al hacer clic fuera
const handleClickOutside = (event) => {
  if (
    showPlayerModal.value &&
    playerContainer.value &&
    !playerContainer.value.contains(event.target)
  ) {
    showPlayerModal.value = false;
  }
};

// ⚠️ Nueva variable para detectar si el usuario pausó manualmente
const userPaused = ref(false);

// 🔒 Variable para controlar operaciones de reproducción en curso
let playPromise = null;

// 🎵 Manejar forzar reproducción de estación
function handleForcePlay(event) {
  const { station } = event.detail;

  if (audioPlayer.value) {
    // Actualizar la URL del stream
    store.setAudioSrc(station.streamUrl);

    // Iniciar reproducción
    store.play(); // Esto activará el watcher que llama a playAudio()
  }
}

// Función para actualizar el show en curso
async function updateCurrentShow() {
  try {
    let showData;

    // Usar el sistema de WordPress para ambas estaciones
    showData = await getCurrentShow(null, currentStationId.value);

    if (showData) {
      currentShow.value = showData;
    }
  } catch (error) {
    console.error("❌ Error updating current show:", error);
  }
}

const { register: registerRadioAudio } = useRadioAudio();

onMounted(async () => {
  // El elemento se comparte: la onda del transporte necesita ESTE, no otro.
  registerRadioAudio(audioPlayer.value);

  // 🔴 **Y de este mismo elemento sale lo que dice el audio.** Los ID3 de la
  // emisión llegan como `cue`s de una pista de texto del `<audio>`, y se
  // disparan cuando ese trozo suena de verdad. Es lo que hace que la ficha
  // cambie a la vez que la canción para cada oyente, tenga el retraso que
  // tenga. Ver `services/id3Emision.js`.
  escuchaLaEmision(audioPlayer.value);
  updateCurrentShow();
  nowPlaying.value = await fetchNowPlaying(currentStationId.value);
  intervalId = setInterval(async () => {
    updateCurrentShow();
    nowPlaying.value = await fetchNowPlaying(currentStationId.value);
  }, 60000); // 60 segundos - optimizado para reducir carga del servidor

  // 🎵 Escuchar eventos de forzar reproducción
  window.addEventListener("forcePlayStation", handleForcePlay);

  // 👆 Agregar listener para cerrar panel al hacer clic fuera
  document.addEventListener("click", handleClickOutside);

  // ⚠️ Solo agregar listeners de reconexión en Desktop
  // En móviles, HLS nativo gestiona buffering/stalled internamente
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (audioPlayer.value && !isMobile) {
    audioPlayer.value.addEventListener("playing", handleConnectionRestored);
    audioPlayer.value.addEventListener("stalled", handleConnectionLoss);
    audioPlayer.value.addEventListener("error", handleConnectionLoss);
    // console.log('🎧 Listeners de reconexión activados (Desktop)');
  } else {
    // console.log('📱 Listeners de reconexión desactivados (Móvil - HLS nativo gestiona buffering)');
  }
});

onUnmounted(() => {
  dejaDeEscuchar();
  if (intervalId) clearInterval(intervalId);

  // Solo remover listeners si se agregaron (Desktop)
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (audioPlayer.value && !isMobile) {
    audioPlayer.value.removeEventListener("playing", handleConnectionRestored);
    audioPlayer.value.removeEventListener("stalled", handleConnectionLoss);
    audioPlayer.value.removeEventListener("error", handleConnectionLoss);
  }

  // 🧹 Limpiar event listener personalizado
  window.removeEventListener("forcePlayStation", handleForcePlay);

  // 🧹 Remover listener de clic fuera
  document.removeEventListener("click", handleClickOutside);

  // 🧹 Limpiar instancia de HLS
  cleanupHls();
});

// Monitorea cambios en la reproducción
watch(
  () => store.isPlaying,
  (newVal) => {
    if (newVal) {
      userPaused.value = false; // ✅ Resetear estado de pausa manual
      playAudio();
    } else {
      stopAudio();
    }
  },
);

// 🔄 NUEVO: Monitorea cambios de estación
watch(currentStationId, async (newStationId) => {
  // Guardar el estado de reproducción ANTES de hacer cambios
  const shouldPlayAfterChange = store.isPlaying;

  // Actualizar nowPlaying para la nueva estación
  nowPlaying.value = await fetchNowPlaying(newStationId);

  // Actualizar el show actual para la nueva estación
  await updateCurrentShow();

  // 🧹 Limpiar HLS anterior PRIMERO (antes de tocar el audio element)
  cleanupHls();

  // 🧹 Luego pausar y limpiar el reproductor actual
  if (audioPlayer.value) {
    // Cancelar reproducción pendiente si existe
    if (playPromise !== null) {
      playPromise
        .then(() => {
          playPromise = null;
        })
        .catch(() => {
          playPromise = null;
        });
    }
    audioPlayer.value.pause();
    // Limpiar el source y cargar estado vacío para resetear completamente
    audioPlayer.value.removeAttribute("src");
    audioPlayer.value.load();
  }

  // Actualizar la URL del stream en el store
  if (currentStation.value?.streamUrl) {
    store.setAudioSrc(currentStation.value.streamUrl);
  }

  // Si debe reproducir después del cambio, iniciar reproducción
  // Esto cubre tanto cambios de estación mientras se reproduce
  // como cambios de estación iniciados desde el modal con play
  if (shouldPlayAfterChange && audioPlayer.value && currentStation.value) {
    // Delay más largo para asegurar que todo esté limpio antes de reproducir
    await new Promise((resolve) => setTimeout(resolve, 100));
    playAudio();
  }
});

// 🧹 Limpiar instancia de HLS
function cleanupHls() {
  if (hls.value) {
    hls.value.destroy();
    hls.value = null;
  }
}

// ── Qué calidad se le da a un móvil ──
//
// 🔴 **Hasta el 24.09.26 el móvil se llevaba la más pesada, y casi siempre.**
// Lo vio el servidor midiendo. Había una función que decidía si era wifi, y
// respondía que sí cuando el tipo era wifi, cuando `effectiveType` era `4g`
// **y cuando no conseguía detectar nada** —que es lo que pasa en Safari y en
// los navegadores incrustados, porque ahí `navigator.connection` no existe—.
// Y «wifi» forzaba `aac_low_320`, que **no es «low»: son 352 kbps**, la más
// alta de las cuatro. En la calle con datos, unos **158 MB por hora**.
//
// 🔴 **La solución no es adivinar mejor: es dejar de adivinar.** El directo se
// sirve con **lista maestra**, así que el reproductor mide el ancho de banda
// real y cambia de variante solo, subiendo y bajando según la cobertura. Eso lo
// hace mejor que nosotros mirando una API que la mitad de los navegadores no
// tiene. El código ya usaba la maestra… pero solo cuando creía que había datos
// móviles, que era casi nunca.
//
//     aac_lofi       52 kbps
//     aac_midfi     105 kbps
//     aac_hifi      211 kbps
//     aac_low_320   352 kbps
//
// ⚠️ **La única decisión que queda es el ahorro de datos.** Si el navegador dice
// que quien mira lo ha pedido, se le da la más ligera y no se negocia: ese ajuste
// es una petición explícita, no una estimación.
function getOptimalHlsUrl(station) {
  if (!station.hlsUrl) return null;

  const conexion =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (conexion?.saveData) {
    const base = station.hlsUrl.replace("/live.m3u8", "");
    return `${base}/aac_lofi.m3u8`;
  }

  // La maestra: que elija el reproductor, que para eso mide.
  return station.hlsUrl;
}

// Iniciar reproducción
function playAudio() {
  if (!audioPlayer.value) return;

  // 🔒 Si ya hay una reproducción en curso, esperar a que termine
  if (playPromise !== null) {
    playPromise
      .then(() => {
        playPromise = null;
        playAudio();
      })
      .catch(() => {
        playPromise = null;
      });
    return;
  }

  isLoading.value = true; // 🔄 Mostrar spinner mientras carga

  // 🎬 Verificar si la estación actual tiene HLS disponible
  const station = currentStation.value;

  // 🔍 Detectar si es móvil
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // 🔴 **WebKit va por HLS nativo y NO se toca.** Esa es la nota que hay en
  // memoria y sigue en pie: pasar el iPhone a hls.js no arreglaría la onda
  // —es el mismo WebKit— y se perdería la persistencia del audio en zonas sin
  // cobertura, que gestiona el HLS nativo y que Pablo señaló como crítica.
  //
  // ⚠️ **Android es otro caso y por eso se separa** (24.09.26, lo midió el
  // servidor). Android **no sabe reproducir HLS de forma nativa**: hasta hoy se
  // le asignaba el `.m3u8` al elemento de audio y la música sonaba **solo
  // porque el `catch` caía al MP3**. Funcionaba, pero tardaba y dependía de que
  // el rechazo llegase como error; el día que llegue de otra forma, silencio.
  // Con hls.js —que ya va en el paquete y que Android Chrome soporta— hay HLS
  // de verdad, con su bitrate adaptativo y con los segmentos que el service
  // worker guarda, que en Android no servían para nada.
  const esWebKit = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (station.hlsUrl && Hls.isSupported() && !esWebKit) {
    // 🎬 Usar HLS.js para streaming adaptativo
    cleanupHls(); // Limpiar instancia anterior si existe

    hls.value = new Hls({
      // Configuración de buffer para conexiones móviles inestables
      maxBufferLength: 60, // 60 segundos de buffer máximo
      maxMaxBufferLength: 120, // 2 minutos en condiciones ideales
      maxBufferSize: 60 * 1000 * 1000, // 60MB
      maxBufferHole: 0.5, // Tolerar huecos de 0.5s
      manifestLoadingRetryDelay: 2000, // Reintentar cada 2s
      levelLoadingRetryDelay: 2000,
      backBufferLength: 30, // Mantener 30s de buffer anterior
      abrEwmaDefaultEstimate: 128000, // Asumir 128kbps por defecto
      liveSyncDurationCount: 3, // Sincronizar con los últimos 3 segmentos (más tolerante)
      liveMaxLatencyDurationCount: 10, // Permitir hasta 10 segmentos de latencia
      enableWorker: true, // Usar Web Worker para mejor rendimiento
      lowLatencyMode: false, // Desactivar modo baja latencia (priorizar estabilidad)
      fragLoadingMaxRetry: 6, // Más reintentos para cargar fragmentos
      manifestLoadingMaxRetry: 6, // Más reintentos para cargar playlist
    });

    hls.value.loadSource(station.hlsUrl);
    hls.value.attachMedia(audioPlayer.value);

    hls.value.on(Hls.Events.MANIFEST_PARSED, () => {
      playPromise = audioPlayer.value
        .play()
        .then(() => {
          isLoading.value = false;
          playPromise = null;
        })
        .catch((error) => {
          if (error.name === "NotAllowedError") {
            console.log(
              "⚠️ Autoplay bloqueado - El usuario debe dar click en play",
            );
            isLoading.value = false;
            playPromise = null;
            store.stop();
            return;
          }
          if (error.name !== "AbortError") {
            console.error("❌ Error al reproducir HLS:", error);
          }
          isLoading.value = false;
          playPromise = null;
        });
    });

    hls.value.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) {
        console.error("❌ Error fatal de HLS:", data);
        isLoading.value = false;

        // Intentar recuperar según el tipo de error
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.log("🔄 Error de red, intentando recuperar...");
            hls.value.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR: {
            // ⚠️ `recoverMediaError()` hace detachMedia + attachMedia por
            // dentro, así que reinicia el elemento igual que `load()` y deja
            // huérfano el nodo de la onda —`createMediaElementSource` solo
            // admite una llamada por elemento, así que no hay forma de
            // rehacerlo—. El síntoma es una onda quieta con la radio sonando
            // perfectamente, y aparece justo en los cortes: locuciones,
            // separadores, huecos.
            //
            // Por eso deja de ser la primera respuesta y pasa a ser la última.
            // Antes se prueba `startLoad()`, que reanuda la carga de segmentos
            // **sin tocar el elemento**: la mayoría de los tropiezos de buffer
            // en directo se arreglan así. Solo si vuelve a fallar dentro del
            // margen —o sea, si el intento suave no bastó— se recurre a la
            // recuperación destructiva.
            //
            // El orden de prioridades no cambia: la emisión manda sobre la
            // onda, siempre. Un error de media sin recuperar deja la radio
            // muda, y eso es peor que una onda parada.
            const ahora = Date.now();
            if (ahora - ultimoIntentoSuave > 8000) {
              ultimoIntentoSuave = ahora;
              console.log("🔄 Error de media: reanudando carga sin tocar el elemento");
              hls.value.startLoad();
              break;
            }
            console.log("🔄 El intento suave no bastó: recuperación completa");
            ultimoIntentoSuave = 0;
            hls.value.recoverMediaError();
            break;
          }
          default:
            // Error irrecuperable, limpiar HLS
            cleanupHls();
            break;
        }
      }
    });
  } else if (
    station.hlsUrl &&
    audioPlayer.value.canPlayType("application/vnd.apple.mpegurl") &&
    !isMobile
  ) {
    // 🍎 Safari Desktop soporta HLS nativo
    // console.log('🍎 Usando HLS nativo de Safari Desktop');

    audioPlayer.value.src = station.hlsUrl;

    playPromise = audioPlayer.value
      .play()
      .then(() => {
        // console.log('✅ HLS nativo reproduciendo');
        isLoading.value = false;
        playPromise = null;
      })
      .catch((error) => {
        if (error.name === "NotAllowedError") {
          console.log(
            "⚠️ Autoplay bloqueado - El usuario debe dar click en play",
          );
          isLoading.value = false;
          playPromise = null;
          store.stop();
          return;
        }

        if (error.name !== "AbortError") {
          console.error("❌ Error al reproducir HLS nativo:", error);

          // Si falla HLS nativo, intentar con MP3 directo como fallback
          console.log("🔄 Fallback a MP3 directo");
          audioPlayer.value.src = station.streamUrl;

          playPromise = audioPlayer.value
            .play()
            .then(() => {
              isLoading.value = false;
              playPromise = null;
            })
            .catch((fallbackError) => {
              if (fallbackError.name === "NotAllowedError") {
                console.log("⚠️ Autoplay bloqueado en fallback");
                isLoading.value = false;
                playPromise = null;
                store.stop();
                return;
              }
              console.error("❌ Error en fallback MP3:", fallbackError);
              isLoading.value = false;
              playPromise = null;
            });
        } else {
          isLoading.value = false;
          playPromise = null;
        }
      });
  } else {
    // 📱 HLS nativo. **Desde el 24.09.26 esto es solo el iPhone**: Android pasa
    // por hls.js, que es lo que sabe reproducir HLS ahí.
    if (isMobile && station.hlsUrl) {
      // Lista maestra: el bitrate lo decide el reproductor midiendo la red.
      // Salvo ahorro de datos, que manda y se lleva la más ligera.
      // Service Worker cachea segmentos HLS para buffer offline adicional

      const optimalUrl = getOptimalHlsUrl(station);

      // console.log('📱 Móvil - Usando HLS nativo:');
      // console.log(`   URL: ${optimalUrl}`);
      // console.log('   ✅ Service Worker proporciona cache offline de 60s');

      audioPlayer.value.src = optimalUrl;

      playPromise = audioPlayer.value
        .play()
        .then(() => {
          // console.log('✅ HLS móvil reproduciendo con ABR nativo');
          isLoading.value = false;
          playPromise = null;
        })
        .catch((error) => {
          // 🚫 Ignorar errores de autoplay bloqueado por el navegador
          if (error.name === "NotAllowedError") {
            console.log(
              "⚠️ Autoplay bloqueado - El usuario debe dar click en play",
            );
            isLoading.value = false;
            playPromise = null;
            store.stop(); // Resetear estado a paused
            return;
          }

          if (error.name !== "AbortError") {
            console.error("❌ Error al reproducir HLS móvil:", error);

            // Fallback a MP3 directo si falla HLS
            console.log("🔄 Fallback a MP3 directo");
            audioPlayer.value.src = station.streamUrl;

            playPromise = audioPlayer.value
              .play()
              .then(() => {
                isLoading.value = false;
                playPromise = null;
              })
              .catch((fallbackError) => {
                // Ignorar errores de autoplay en fallback también
                if (fallbackError.name === "NotAllowedError") {
                  console.log("⚠️ Autoplay bloqueado en fallback MP3");
                  isLoading.value = false;
                  playPromise = null;
                  store.stop();
                  return;
                }
                console.error("❌ Error en fallback MP3:", fallbackError);
                isLoading.value = false;
                playPromise = null;
              });
          } else {
            isLoading.value = false;
            playPromise = null;
          }
        });
    } else {
      // 📻 MP3 directo para navegadores sin soporte HLS
      // console.log('📻 Usando MP3 directo (sin HLS)');
      store.setAudioSrc(station.streamUrl);
      audioPlayer.value.src = store.audioSrc;

      playPromise = audioPlayer.value
        .play()
        .then(() => {
          // console.log('✅ MP3 directo reproduciendo');
          isLoading.value = false;
          playPromise = null;
        })
        .catch((error) => {
          // 🚫 Ignorar errores de autoplay bloqueado por el navegador
          if (error.name === "NotAllowedError") {
            console.log(
              "⚠️ Autoplay bloqueado - El usuario debe dar click en play",
            );
            isLoading.value = false;
            playPromise = null;
            store.stop();
            return;
          }
          if (error.name !== "AbortError") {
            console.error("❌ Error al reproducir MP3:", error);
          }
          isLoading.value = false;
          playPromise = null;
        });
    }
  }
}

// **🔴 Corregido: Stop no activa el spinner**
function stopAudio() {
  if (audioPlayer.value) {
    // Cancelar cualquier reproducción pendiente
    if (playPromise !== null) {
      playPromise
        .then(() => {
          playPromise = null;
        })
        .catch(() => {
          playPromise = null;
        });
    }

    userPaused.value = true; // ✅ Marcar que el usuario pausó manualmente

    // 🧹 Limpiar HLS si está activo
    cleanupHls();

    audioPlayer.value.pause();
    audioPlayer.value.src = "";
    isLoading.value = false; // ✅ Asegurar que el spinner se oculta al pausar
    playPromise = null; // Reset de la promesa
  }
}

// Alternar entre play y pausa
function togglePlayback() {
  console.log('🎮 togglePlayback llamado. Estado actual:', {
    isPlaying: store.isPlaying,
    audioSrc: store.audioSrc,
    currentStationId: currentStationId.value,
    audioPlayerExists: !!audioPlayer.value
  });

  if (store.isPlaying) {
    console.log('⏸️ Deteniendo reproducción');
    store.stop();
  } else {
    console.log('▶️ Iniciando reproducción, llamando store.play()');
    store.play();

    // Actualizar el background en paralelo (sin bloquear la reproducción)
    getCurrentShow(null, currentStationId.value)
      .then((showData) => {
        if (showData && showData.image) {
          setShowImage(showData.image);
        }
      })
      .catch((error) => {
        console.error("Error updating background:", error);
      });
  }

  console.log('🔊 togglePlayback - isPlaying después:', store.isPlaying);
}

// Toggle del panel expandido del reproductor
function openPlayerModal() {
  showPlayerModal.value = !showPlayerModal.value;
}

// ✅ Se detecta que la radio está reproduciéndose sin problemas
function handleConnectionRestored() {
  isLoading.value = false; // ✅ Ocultar spinner si la radio ya está sonando
}

// 🔴 Se detecta una pérdida de conexión (pero no si el usuario pausó)
function handleConnectionLoss() {
  if (userPaused.value) {
    return;
  }

  isLoading.value = true; // 🔄 Mostrar el spinner si hay una desconexión
  intentarReconectar();
}

// 🔄 Intentar reconectar cada 3 segundos
function intentarReconectar() {
  setTimeout(() => {
    if (!navigator.onLine) {
      return intentarReconectar();
    }
    if (audioPlayer.value && !userPaused.value) {
      // 🔴 Con HLS NO se llama a load(), y esa línea costó una onda muerta.
      //
      // `load()` destruye el MediaSource que hls.js tiene enganchado y reinicia
      // el elemento entero. La emisión vuelve —hls.js reengancha— pero el
      // `MediaElementAudioSourceNode` de la onda se queda huérfano: solo se
      // puede crear UNA vez por elemento, así que no hay forma de rehacerlo, y
      // el analizador devuelve silencio para siempre.
      //
      // El síntoma engaña: la radio se oye perfectamente y la onda se queda
      // plana, sin un solo error en consola. Aparece justo en los cortes —una
      // locución, un separador, un hueco— porque son los que provocan el
      // `stalled` que trae hasta aquí.
      //
      // `startLoad()` es la reconexión propia de hls.js: reanuda la carga de
      // segmentos sin tocar el elemento ni el MediaSource. Solo se recurre a
      // load() cuando no hay HLS, que es el caso del MP3 directo.
      if (hls.value) {
        try {
          hls.value.startLoad();
        } catch (error) {
          audioPlayer.value.load();
        }
      } else {
        audioPlayer.value.load();
      }

      playPromise = audioPlayer.value
        .play()
        .then(() => {
          isLoading.value = false; // ✅ Ocultar el spinner cuando la radio se reconecte
          playPromise = null;
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            console.error("Error al reconectar:", err);
          }
          playPromise = null;
        });
    }
  }, 3000);
}
</script>

<template>
  <div ref="playerContainer" class="aa-controls" @click="openPlayerModal">
    <!-- crossorigin no es opcional: sin él el elemento queda «manchado» y el
         AnalyserNode de la onda devuelve silencio absoluto, sin ningún error.
         listen.astra.fm refleja el Origin, así que la cabecera existe. -->
    <audio ref="audioPlayer" crossorigin="anonymous">
      Your browser does not support the audio element.
    </audio>
    <div class="aa-player-image">
      <img
        :src="arte(currentShow?.image || astraPlaceHolder)"
        :alt="currentShow?.name"
        @error="$event.target.src = arte(astraPlaceHolder)"
      />
    </div>
    <div class="aa-divider-container">
      <div class="aa-now-playing">
        <div class="show-and-status">
          <p class="aa-title-m">{{ currentShow.name }}</p>
          <div class="status-icon">
            <div class="circle"></div>
            <span class="label">En el aire</span>
          </div>
        </div>
        <div class="aa-caption">
          <span>{{ formatRange(currentShow?.range) }}</span>
        </div>
      </div>
      <div class="aa-divider"></div>
    </div>
    <div class="aa-play-btn" @click.stop="togglePlayback">
      <!-- 🔄 Spinner si está cargando -->
      <button v-if="isLoading" disabled>
        <i class="fa-solid fa-spinner fa-spin"></i>
      </button>
      <!-- ▶ Play -->
      <button v-else-if="!store.isPlaying">
        <i class="fa-solid fa-play"></i>
      </button>
      <!-- ⏸ Pause -->
      <button v-else>
        <i class="fa-solid fa-pause"></i>
      </button>
    </div>
    <div class="aa-plus" @click.stop="openPlayerModal">
      <div>
        <i
          class="aa-icon"
          :class="showPlayerModal ? 'fa-solid fa-minus' : 'fa-solid fa-plus'"
        ></i>
      </div>
    </div>

    <!-- Panel expandido del reproductor -->
    <PlayerModal
      :show="showPlayerModal"
      :current-show="currentShow"
      @close="showPlayerModal = false"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "~@/assets/scss/astra-vars.scss";

// ----------- PLAYER -------------

// on air
.aa-play-btn {
  .fa-spinner {
    font-size: 24px;
    color: var(--text-color-invert); /* Color destacado */
  }
  button {
    width: 30px;
  }
}

.show-and-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.status-icon {
  display: flex;
  align-items: center;
  margin-left: 10px;

  .circle {
    width: 10px;
    height: 10px;
    border-radius: 0;
    background-color: #ff6600;
  }

  .label {
    margin-left: 5px;
    font-size: 10px; // Ajusta entre 10px y 12px según necesidades
    @media (min-width: $break-desktop) {
      font-size: 12px; // Ajusta para pantallas más grandes
    }
  }
}

// cierra on air

:root {
  --main-dark: #25272a;
  --neu-dark: #090a0c;
  --neu-light: #252a30;
  --back-gradient: linear-gradient(
    180deg,
    rgba(62, 67, 76, 1) 0%,
    rgba(30, 32, 36, 1) 100%
  );
}
.aa-controls {
  position: relative; // Para posicionar el panel expandido
  margin-top: $s10;
  margin-bottom: $s08;
  color: var(--text-color);
  flex-wrap: wrap;
  display: none; // Oculto completamente - se usa el player del header
  align-items: center; // Alinea los elementos verticalmente en el centro
  justify-content: space-between; // Espacia los elementos equitativamente
  border-radius: 0;
  border: 1px solid var(--border);
  background-color: var(--bg-cards);
  backdrop-filter: blur(15px);
  cursor: pointer;

  @media (min-width: $break-desktop) {
    max-width: fit-content;
    min-width: 350px;
  }
  .aa-divider-container {
    // Ajustes específicos para aa-divider-container si son necesarios
    flex-grow: 1; // Permite que el contenedor crezca si es necesario
    display: none;
  }
  .aa-plus {
    padding-right: $s12;
    padding-top: $s12;
    padding-bottom: $s12;
    color: var(--text-color);
  }
  .aa-play-btn {
    padding-left: $s04;
    padding-top: $s04;
    padding-bottom: $s04;
    padding-right: $s04;
    margin-right: $s12;
    border-radius: 0;
    border: 1px solid var(--border);
    background-color: var(--bg-color-invert);
  }
  .aa-play-btn,
  .aa-plus {
    .fas,
    .fa-solid {
      font-size: 18px;
      cursor: pointer;
      margin-left: 2px;
    }
  }
  .aa-player-image {
    padding: 4px;
    padding-right: $s12;
    img {
      width: 40px;
      height: 40px;
      border-radius: 0;
      object-fit: cover;
    }
  }
  .aa-now-playing {
    p {
      margin: 0;
    }
  }
  .aa-player-show-description {
    order: 2; /* Coloca este elemento al final */
    width: 100%; /* Ocupa el ancho completo */
    padding: $s14;
    position: absolute;
    top: 55px;
    background-color: var(--bg-cards);
    //box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(15px);
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
    right: 0.5px;
    img {
      width: 100%;
      border-radius: 0;
    }
  }
  @media (min-width: $break-desktop) {
    .aa-divider-container {
      display: block;
      padding-right: $s14;
    }
    .aa-play-btn {
      padding-left: $s04;
      padding-top: $s04;
      padding-bottom: $s04;
      padding-right: $s04;
      margin-right: $s12;
      background-color: var(--bg-color-invert);
    }
    .aa-player-image {
      padding: 4px;
      padding-right: $s16;
      img {
        width: 50px;
        height: 50px;
        border-radius: 0;
        object-fit: cover;
      }
    }
    .aa-plus {
      padding-top: 0;
      padding-bottom: 0;
      padding-right: $s16;
      color: var(--text-color);
    }
  } /* Escritorios medianos y arriba */
}
</style>
